import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoTopKeywords } from '../utils/demoData'
import {
  Brain,
  ArrowRight,
  Flame,
  BookOpen,
  GitCompare,
  Lightbulb,
  Target,
  Search,
  MessageSquare,
  Sparkles,
  Tag,
  Copy,
  Check
} from 'lucide-react'

interface QueryObjective {
  intentType: string
  queryCount: number
  percentage: string
  avgCommercialIntent: string
}

interface IntelligenceData {
  totalQueries: number
  userMentionRate: string
  intentBreakdown: QueryObjective[]
}

interface TopCategory {
  id: number
  name: string
  slug: string
  queryCount: number
  topKeywords: string[]
  topTopics: string[]
}

export default function SearchIntelligenceOverview() {
  const { activeBrand } = useAuth()
  const [data, setData] = useState<IntelligenceData | null>(null)
  const [loading, setLoading] = useState(true)
  const [topCategories, setTopCategories] = useState<TopCategory[]>([])
  const [copiedKeyword, setCopiedKeyword] = useState<string | null>(null)
  const { isDemoMode, withDemoParam } = useDemoMode()

  useEffect(() => {
    if (!activeBrand?.userBrandId && !isDemoMode) return

    const fetchIntelligenceData = async () => {
      try {
        setLoading(true)

        // Use demo data if in demo mode
        if (isDemoMode) {
          setData({
            totalQueries: 156,
            userMentionRate: '55.8',
            intentBreakdown: [
              {
                intentType: 'Discovery',
                queryCount: 54,
                percentage: '34.6',
                avgCommercialIntent: '42'
              },
              {
                intentType: 'Comparison',
                queryCount: 38,
                percentage: '24.4',
                avgCommercialIntent: '68'
              },
              {
                intentType: 'Purchase Intent',
                queryCount: 28,
                percentage: '17.9',
                avgCommercialIntent: '85'
              },
              {
                intentType: 'Problem Solving',
                queryCount: 22,
                percentage: '14.1',
                avgCommercialIntent: '55'
              },
              {
                intentType: 'Research',
                queryCount: 14,
                percentage: '9.0',
                avgCommercialIntent: '38'
              }
            ]
          })
          setLoading(false)
          return
        }

        const response = await api.get(`/intelligence/overview?userBrandId=${activeBrand!.userBrandId}`)

        // Use demo data if no real data exists
        if (!response.data || response.data.totalQueries === 0) {
          setData({
            totalQueries: 156,
            userMentionRate: '55.8',
            intentBreakdown: [
              {
                intentType: 'Discovery',
                queryCount: 54,
                percentage: '34.6',
                avgCommercialIntent: '42'
              },
              {
                intentType: 'Comparison',
                queryCount: 38,
                percentage: '24.4',
                avgCommercialIntent: '68'
              },
              {
                intentType: 'Purchase Intent',
                queryCount: 28,
                percentage: '17.9',
                avgCommercialIntent: '85'
              },
              {
                intentType: 'Problem Solving',
                queryCount: 22,
                percentage: '14.1',
                avgCommercialIntent: '55'
              },
              {
                intentType: 'Research',
                queryCount: 14,
                percentage: '9.0',
                avgCommercialIntent: '38'
              }
            ]
          })
        } else {
          setData(response.data)
        }
      } catch (error: any) {
        console.error('Failed to fetch intelligence data:', error)
        // Use demo data as fallback when API fails
        setData({
          totalQueries: 0,
          userMentionRate: '0',
          intentBreakdown: []
        })
      } finally {
        setLoading(false)
      }
    }

    fetchIntelligenceData()
  }, [activeBrand?.userBrandId, isDemoMode])

  useEffect(() => {
    if (!activeBrand?.userBrandId && !isDemoMode) return

    const fetchTopKeywords = async () => {
      try {
        // Use demo data if in demo mode
        if (isDemoMode) {
          setTopCategories(demoTopKeywords)
          return
        }

        const response = await api.get(`/categories/${activeBrand!.userBrandId}/top-keywords?limit=3`)
        if (response.data.success) {
          setTopCategories(response.data.categories)
        }
      } catch (error: any) {
        console.error('Failed to fetch top keywords:', error)
      }
    }

    fetchTopKeywords()
  }, [activeBrand?.userBrandId, isDemoMode])

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopiedKeyword(text)
    setTimeout(() => setCopiedKeyword(null), 2000)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // Show empty state if no data
  if (!data || data.totalQueries === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Search Intelligence</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Unlock AI-powered insights about your market's search behavior and buying intent.
        </p>
        <Link
          to={withDemoParam('/search-queries')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Start Analyzing Queries
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    )
  }

  // Calculate buying signal
  const buyingSignal = data.intentBreakdown.length > 0
    ? Math.round(
        data.intentBreakdown.reduce((sum, i) => sum + parseFloat(i.avgCommercialIntent), 0) / data.intentBreakdown.length
      )
    : 0

  const isHigh = buyingSignal >= 60
  const isMedium = buyingSignal >= 30 && buyingSignal < 60
  const isLow = buyingSignal < 30

  // Determine gradient colors
  let gradientFrom = 'from-blue-500'
  let gradientTo = 'to-blue-700'
  if (isHigh) {
    gradientFrom = 'from-red-500'
    gradientTo = 'to-red-700'
  } else if (isMedium) {
    gradientFrom = 'from-orange-500'
    gradientTo = 'to-orange-600'
  }

  // Get top 3 query objectives
  const topObjectives = [...data.intentBreakdown]
    .sort((a, b) => b.queryCount - a.queryCount)
    .slice(0, 3)

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Brain className="w-5 h-5 text-indigo-600 mr-2" />
              Search Intelligence Overview
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              AI-powered insights from {data.totalQueries} analyzed queries
            </p>
          </div>
          <Link
            to={withDemoParam('/search')}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
          >
            View Full Report
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Market Intelligence Score */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow p-5 text-white">
            <h3 className="text-base font-semibold mb-1">Market Intelligence Score</h3>
            <div className="flex items-baseline space-x-2 mb-3">
              <span className="text-4xl font-bold">
                {Math.round((parseFloat(data.userMentionRate) / 100) * 100)}
              </span>
              <span className="text-lg">/100</span>
            </div>
            <p className="text-indigo-100 text-xs">
              Your AI visibility across all tracked queries. Based on your {data.userMentionRate}% mention rate.
            </p>
          </div>

          {/* Market Buying Signal */}
          <div className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-lg shadow p-5 text-white`}>
            <h3 className="text-base font-semibold mb-1">Market Buying Signal</h3>
            <div className="flex items-baseline space-x-2 mb-3">
              <span className="text-4xl font-bold">{buyingSignal}</span>
              <span className="text-lg">/100</span>
            </div>

            <div className="space-y-2">
              {/* Stage */}
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  {isLow && <BookOpen className="w-4 h-4 text-white" />}
                  {isMedium && <GitCompare className="w-4 h-4 text-white" />}
                  {isHigh && <Flame className="w-4 h-4 text-white" />}
                </div>
                <p className="ml-2 text-sm font-semibold text-white">
                  {isLow && 'Low intent - early education phase'}
                  {isMedium && 'Medium intent - researching & comparing'}
                  {isHigh && 'High intent - actively evaluating'}
                </p>
              </div>

              {/* Meaning */}
              <div className="flex items-start text-xs">
                <Lightbulb className="w-3 h-3 text-white/80 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-white/90">
                  {isLow && 'Searchers are learning about the problem space and possible solutions.'}
                  {isMedium && 'Searchers are actively comparing solutions but not yet ready to buy.'}
                  {isHigh && 'Searchers are in late-stage consideration or ready to purchase.'}
                </p>
              </div>

              {/* Action */}
              <div className="flex items-start text-xs">
                <Target className="w-3 h-3 text-white/80 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-white/90">
                  {isLow && 'Focus on educational content, thought leadership, and awareness.'}
                  {isMedium && 'Create comparison content, case studies, and feature highlights.'}
                  {isHigh && 'Focus on conversion content, pricing, and competitive differentiation.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Top 3 Query Objectives */}
        {topObjectives.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <MessageSquare className="w-4 h-4 text-gray-600 mr-2" />
              Top Query Objectives
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topObjectives.map((objective, idx) => (
                <div
                  key={objective.intentType}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                    <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                      {objective.percentage}%
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    {objective.intentType}
                  </h4>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>{objective.queryCount} queries</span>
                    <span className="text-orange-600 font-medium">
                      {objective.avgCommercialIntent}/100 intent
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEO  Recommendations */}
        {topCategories.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                <Sparkles className="w-4 h-4 text-indigo-600 mr-2" />
                TOP SEO Recommendations
              </h3>
              <Link
                to={withDemoParam('/search-intelligence')}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors flex items-center"
              >
                View All
                <ArrowRight className="inline-block w-4 h-4 ml-1" />
              </Link>
            </div>

            <p className="text-xs text-gray-600 mb-4">
              Top search categories to build content around for better AI visibility.
            </p>

            <div className="space-y-3">
              {topCategories.map((category) => (
                <div key={category.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <Tag className="w-3.5 h-3.5 text-indigo-600" />
                    <h4 className="text-sm font-semibold text-gray-900">{category.name}</h4>
                    <span className="text-xs text-gray-500">({category.queryCount} queries)</span>
                  </div>
                  <div className="space-y-3">
                    {/* Keywords */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-2">Keywords:</p>
                      <div className="flex flex-wrap gap-2">
                        {category.topKeywords.map((keyword, idx) => {
                          const isCopied = copiedKeyword === keyword
                          return (
                            <button
                              key={idx}
                              onClick={() => handleCopyToClipboard(keyword)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white text-indigo-700 text-xs font-medium rounded-lg border border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all cursor-pointer group"
                            >
                              <span>{keyword}</span>
                              {isCopied ? (
                                <Check className="w-3 h-3 text-green-600" />
                              ) : (
                                <Copy className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Suggested Topics */}
                    {category.topTopics && category.topTopics.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-2">Suggested Content Topics:</p>
                        <div className="flex flex-wrap gap-2">
                          {category.topTopics.slice(0, 2).map((topic, idx) => {
                            const isCopied = copiedKeyword === topic
                            return (
                              <button
                                key={idx}
                                onClick={() => handleCopyToClipboard(topic)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-lg border border-purple-200 hover:bg-purple-100 hover:border-purple-300 transition-all cursor-pointer group"
                              >
                                <span>{topic}</span>
                                {isCopied ? (
                                  <Check className="w-3 h-3 text-green-600" />
                                ) : (
                                  <Copy className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity" />
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}
      </div>
    </div>
  )
}
